import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { reverseGeocodeAction } from '../../../v2/Address/APIs/action';
import ReusableModal from '../../../v2/MUI_custom_component/Modal/CustomModal';
import LocationMapSelector from '../../../v2/Address/Screens/LocationMapSelector';
import AddressFormModal from '../../../v2/Address/Screens/AddressFormModal';
import CustomButton from '../../../v2/MUI_custom_component/Button/CustomButton';
import IconReusable from '../../../components/IconReusable';
import { useSelector } from 'react-redux';

const AddAddressGPS = ({ isOpen, onClose, onSuccess, onAddressAdded, currentFirm, editingAddress, isEditMode }) => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 1200px)');

    // GPS FLOW STATE
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [addressFormModalOpen, setAddressFormModalOpen] = useState(false);
    const [mapCenter, setMapCenter] = useState(null);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [preloadedAddressData, setPreloadedAddressData] = useState(null);
    const [isAddressBeingEdited, setIsAddressBeingEdited] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const { selectedEntity: selectedCompanyEntity } = useSelector((state) => state.entityReducer);
    const handleSetLocationClick = () => {
        if (navigator.geolocation) {
            setIsLocationLoading(true);
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    setMapCenter([latitude, longitude]);
                    setMarkerPosition([latitude, longitude]);
                    try {
                        const { data, error, message } = await reverseGeocodeAction(
                            latitude,
                            longitude,
                        );
                        if (!error) {
                            setPreloadedAddressData(data);
                        } else {
                            toast.error(message);
                            setPreloadedAddressData(null);
                        }
                    } catch (error) {
                        setPreloadedAddressData(null);
                    }

                    setLocationModalOpen(true);
                    setIsLocationLoading(false);
                },
                (error) => {
                    setIsLocationLoading(false);
                    setPreloadedAddressData(null);

                    // Check if user denied permission
                    if (error.code === error.PERMISSION_DENIED) {
                        // setMapCenter([28.7041, 77.1025]);
                        setAddressFormModalOpen(true);
                    } else {
                        // For other errors, open the location map selector
                        setLocationModalOpen(true);
                    }
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 600000,
                },
            );
        } else {
            setPreloadedAddressData(null);
            setLocationModalOpen(true);
        }
    };

    const handleMapClick = (e) => {
        const { lat, lng } = e.latlng;
        setMarkerPosition([lat, lng]);
    };

    const handleCurrentLocation = (location) => {
        const { lat, lng } = location.latlng;
        setMapCenter([lat, lng]);
        setMarkerPosition([lat, lng]);
    };

    const handleLocationSubmit = (addressData) => {
        setLocationModalOpen(false);
        toast.success('Location selected successfully!');
        if (onSuccess) {
            onSuccess(addressData);
        } else {
            navigate('/address');
        }
    };

    const handleLocationModalClose = () => {
        setLocationModalOpen(false);
        if (onClose) {
            onClose();
        }
    };

    const handleAddressFormModalClose = () => {
        setAddressFormModalOpen(false);
        if (onClose) {
            onClose();
        }
    };

    const handleAddressFormSave = (addressData) => {
        setAddressFormModalOpen(false);
        setSelectedData(addressData);
        // toast.success('Address saved successfully!');    
        if (onSuccess) {
            onSuccess(addressData);
        } else {
            navigate('/address');
        }
    };

    const handleBack = () => {
        if (onClose) {
            onClose();
        } else {
            navigate('/address');
        }
    };

    // Start GPS flow when component opens (skip if editing)
    useEffect(() => {
        if (isOpen) {
            if (isEditMode && editingAddress) {
                // If editing, go directly to address form with prefilled data
                setPreloadedAddressData(editingAddress);
                setIsAddressBeingEdited(true);
                setAddressFormModalOpen(true);
            } else {
                // For new address, start GPS flow
                handleSetLocationClick();
            }
        } else {
            // Reset state when modal closes
            setPreloadedAddressData(null);
            setIsAddressBeingEdited(false);
        }
    }, [isOpen, isEditMode, editingAddress]);
    if (!isOpen) {
        return null;
    }
    

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {!isLocationLoading && !locationModalOpen && !addressFormModalOpen && !isEditMode && (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="400px"
                >
                    <Typography variant="h3" mb={3}>
                        Choose Address Location
                    </Typography>
                    <CustomButton
                        variant="contained"
                        onClick={handleSetLocationClick}
                        label="Use GPS Location"
                        leftIcon={() => <IconReusable name="LocationMarker" />}
                    />
                </Box>
            )}

            {/* Location Map Selector Modal */}
            <ReusableModal
                open={locationModalOpen}
                onClose={handleLocationModalClose}
                showHeader={false}
                maxHeight={isMobile ? '100vh' : '589px'}
                topMobile={'0%'}
                topDesktop={'10%'}
                scrollable={false}
                pt={0}
                px={0}
                pb={0}
                maxWidth={isMobile ? '100%' : '618px'}
                centerAlign={!isMobile}
            >
                <LocationMapSelector
                    mapCenter={mapCenter}
                    markerPosition={markerPosition}
                    onMapClick={handleMapClick}
                    onCurrentLocation={handleCurrentLocation}
                    onConfirmLocation={handleLocationSubmit}
                    onCloseModal={handleLocationModalClose}
                    preloadedAddressData={preloadedAddressData}
                    non_reward={true}
                />
            </ReusableModal>

            {/* Address Form Modal */}
            <ReusableModal
                open={addressFormModalOpen}
                onClose={handleAddressFormModalClose}
                showHeader={false}
                maxHeight={isMobile ? '100vh' : '589px'}
                topMobile={'0%'}
                topDesktop={'10%'}
                scrollable={false}
                pt={0}
                px={0}
                pb={0}
                maxWidth={isMobile ? '100%' : '618px'}
                centerAlign={!isMobile}
            >
                <AddressFormModal
                    onBack={handleAddressFormModalClose}
                    onSaveAddress={handleAddressFormSave}
                    addressDetails={isEditMode && editingAddress ? editingAddress : selectedData}
                    isAddressBeingEdited={isEditMode || isAddressBeingEdited}
                    non_reward={true}
                    companyEsiID={selectedCompanyEntity}
                    onAddressAdded={onAddressAdded}
                    coords={null}
                />
            </ReusableModal>
        </Box>
    );
};

export default AddAddressGPS;